import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { SoundProvider } from './components/SoundEffects';
import ProtectedRoute from './components/ProtectedRoute';
import CustomCursor from './components/CustomCursor';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Process from './components/Process';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';

// Heavy / rarely-visited routes — code-split so the marketing home page
// doesn't have to download Three.js, R3F, or the admin bundle on first load.
const Experience3D = lazy(() => import('./components/Experience3D'));
const Analytics = lazy(() => import('./components/Analytics'));
const AdminPortfolio = lazy(() => import('./components/AdminPortfolio'));
const Login = lazy(() => import('./components/Login'));

const RouteFallback = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div
      className="text-cyan-300 text-xs tracking-[0.5em] uppercase animate-pulse"
      style={{ fontFamily: "'Orbitron', sans-serif" }}
    >
      Loading…
    </div>
  </div>
);

// Lightweight per-route SEO updater — sets <title> + meta description +
// canonical href on each navigation. Uses native DOM APIs so we don't need
// an extra library (react-helmet-async etc).
const SEO_BY_PATH = {
  '/': {
    title:
      'Innovative Custom Designs | Brand Strategy, Logos & Custom Web Design',
    description:
      'Strategic brand identity, custom website design & digital marketing for ambitious businesses. Logos, landing pages, brand development, automation & more.',
  },
  '/experience': {
    title:
      'Brand Universe — Interactive 3D Service Map | Innovative Custom Designs',
    description:
      'Explore our services in an immersive 3D galaxy. Multi-select the ones that fit your business and request a tailored proposal — no calls required.',
  },
  '/login': {
    title: 'Sign In | ICD Designs',
    description: 'Admin sign-in for Innovative Custom Designs.',
    noindex: true,
  },
  '/analytics': {
    title: 'Analytics | ICD Designs',
    description: 'Internal analytics dashboard.',
    noindex: true,
  },
  '/admin/portfolio': {
    title: 'Portfolio Admin | ICD Designs',
    description: 'Internal portfolio management.',
    noindex: true,
  },
};

const setMeta = (name, content, attr = 'name') => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const RouteHead = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const cfg = SEO_BY_PATH[pathname] || SEO_BY_PATH['/'];
    document.title = cfg.title;
    setMeta('description', cfg.description);
    setMeta('og:title', cfg.title, 'property');
    setMeta('og:description', cfg.description, 'property');
    setMeta(
      'og:url',
      `${window.location.origin}${pathname}`,
      'property'
    );
    setMeta('twitter:title', cfg.title);
    setMeta('twitter:description', cfg.description);
    setMeta(
      'robots',
      cfg.noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );

    // Canonical link
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      'href',
      `${window.location.origin}${pathname === '/' ? '/' : pathname}`
    );
  }, [pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <SoundProvider>
        <BrowserRouter>
          <div className="App">
            <CustomCursor />
            <Toaster position="top-right" theme="dark" />
            <RouteHead />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Navbar />
                      <Hero />
                      <Services />
                      <About />
                      <Process />
                      <Portfolio />
                      <Testimonials />
                      <Contact />
                      <Footer />
                    </>
                  }
                />
                <Route path="/experience" element={<Experience3D />} />
                <Route path="/login" element={<Login />} />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/portfolio"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPortfolio />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </div>
        </BrowserRouter>
      </SoundProvider>
    </AuthProvider>
  );
}

export default App;
